import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { EventProductsService, ProductImageOption } from 'src/app/services/event-products/event-products.service';
import { normalizeText } from 'src/app/shared/product-import/product-import.model';

/** Lo que devuelve el modal al elegir una imagen. */
export interface PickedProductImage {
    url: string;
    name: string;
    key: string;
}

/**
 * Elegir la imagen de un producto que ya existe para reutilizarla en uno
 * nuevo. Los productos de evento se crean una y otra vez (otra barra, otro
 * precio, otro evento) para cosas que ya se vendieron y ya tienen foto; aquí
 * se toma esa foto sin volver a subirla. Primero lo que hay en este evento
 * (todas las barras); con dos letras se busca además en toda la tienda.
 */
@Component({
    selector: 'app-product-image-picker',
    templateUrl: './product-image-picker.component.html',
    styleUrls: ['./product-image-picker.component.scss'],
})
export class ProductImagePickerComponent implements OnInit, OnDestroy {

    @Input() eventKey: string = '';
    /** Normalmente el nombre que ya se escribió en el formulario. */
    @Input() initialQuery: string = '';

    query = '';
    eventImages: ProductImageOption[] = [];
    storeImages: ProductImageOption[] = [];
    loadingEvent = true;
    searchingStore = false;
    error = '';
    /** La última búsqueda que se mandó a la tienda, para no repetirla ni mostrar resultados viejos. */
    private storeQuery = '';
    private debounce: any = null;

    constructor(
        private modalCtrl: ModalController,
        private service: EventProductsService,
    ) { }

    async ngOnInit() {
        this.query = String(this.initialQuery || '').trim();
        try {
            this.eventImages = await this.service.getEventProductImages(this.eventKey);
        } catch (e) {
            console.error('No se pudieron leer las imágenes del evento:', e);
            this.error = 'No se pudieron leer las imágenes de este evento.';
        }
        this.loadingEvent = false;
        this.searchStore();
    }

    ngOnDestroy() {
        if (this.debounce) clearTimeout(this.debounce);
    }

    close(data?: PickedProductImage) {
        this.modalCtrl.dismiss(data);
    }

    pick(option: ProductImageOption) {
        this.close({ url: option.url, name: option.name, key: option.key });
    }

    setQuery(value: string) {
        this.query = String(value || '');
        if (this.debounce) clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.searchStore(), 350);
    }

    clearQuery() {
        this.setQuery('');
    }

    get hasQuery(): boolean {
        return normalizeText(this.query).length > 0;
    }

    /** Todas las palabras escritas deben aparecer en alguno de los nombres. */
    private matches(option: ProductImageOption, words: string[]): boolean {
        if (words.length === 0) return true;
        const haystack = normalizeText(option.names.join(' '));
        return words.every(w => haystack.indexOf(w) >= 0);
    }

    private get words(): string[] {
        return normalizeText(this.query).split(' ').filter(w => w.length > 0);
    }

    get visibleEventImages(): ProductImageOption[] {
        const words = this.words;
        return this.eventImages.filter(o => this.matches(o, words));
    }

    /** Lo de la tienda que no esté ya entre lo del evento (misma URL). */
    get visibleStoreImages(): ProductImageOption[] {
        const seen: { [url: string]: boolean } = {};
        this.eventImages.forEach(o => seen[o.url] = true);
        return this.storeImages.filter(o => !seen[o.url]);
    }

    get canSearchStore(): boolean {
        return normalizeText(this.query).length >= 2;
    }

    private async searchStore() {
        const term = this.query.trim();
        if (term.length < 2) {
            this.storeImages = [];
            this.storeQuery = '';
            this.searchingStore = false;
            return;
        }
        if (term === this.storeQuery) return;
        this.storeQuery = term;
        this.searchingStore = true;
        try {
            const results = await this.service.searchStoreProductImages(term);
            // Si mientras tanto se escribió otra cosa, estos resultados ya no sirven.
            if (this.storeQuery === term) this.storeImages = results;
        } catch (e) {
            console.error('No se pudo buscar en la tienda:', e);
            if (this.storeQuery === term) {
                this.storeImages = [];
                this.error = 'No se pudo buscar en el resto de la tienda. Prueba de nuevo en un momento.';
            }
        }
        if (this.storeQuery === term) this.searchingStore = false;
    }

    usedIn(option: ProductImageOption): string {
        if (option.count <= 1) return '';
        return `en ${option.count} productos`;
    }
}
